import React, { useMemo, useRef, useState } from 'react';
import { Button, ReadOnlyGuard, SparkIcon, Spinner, TooltipTrigger, UploadArea, useReadOnly } from '@spark';
import { TaskType } from '@/shared/domain/taskTypes';
import { getUserFacingDatasetName } from '@/features/datasets/utils/datasetCloneNames';
import {
    partitionFilesBySupport,
    showUnsupportedFileTypeToast,
    unsupportedFileTypeCardMessage,
} from '@/features/datasets/utils/unsupportedFileHandling';
import {
    datasetSourcePillLabel,
    shouldShowAiGeneratedFileCard,
} from '@/features/datasets/utils/datasetSourceConfig';
import DatasetSourceDetailsModal from '@/features/datasets/components/DataCollection/DatasetSourceDetails/DatasetSourceDetailsModal';
import { formatFileSize } from './useWizardDataUpload';

export const UPLOAD_CARD_HELP = {
    dataset:
        'Dataset is the foundational knowledge base. The model processes these examples to understand the patterns, vocabulary, and structure of your specific content world.',
    benchmark:
        'Benchmark is our trusted baseline. These are verified, top-quality examples used as a strict standard to test how accurately the model applies what it learned from the dataset.',
};

const CARD_COPY = {
    dataset: {
        emptyTitle: 'Upload Dataset',
        filledTitle: 'Dataset file',
        buttonLabel: 'Upload dataset file',
        addMoreLabel: 'Add more files',
        illustration: '/images/upload-dataset-illustration.svg',
        helpText: UPLOAD_CARD_HELP.dataset,
        helpLabel: 'About dataset',
    },
    benchmark: {
        emptyTitle: 'Upload Benchmark',
        filledTitle: 'Benchmark File',
        buttonLabel: 'Upload benchmark file',
        addMoreLabel: 'Add more files',
        illustration: '/images/upload-benchmark-illustration.svg',
        helpText: UPLOAD_CARD_HELP.benchmark,
        helpLabel: 'About benchmark',
    },
};

function UploadCardHelp({ text, label }) {
    return (
        <TooltipTrigger text={text} placement="top" className="cw-dc-upload-card__help">
            <span className="cw-help-icon" tabIndex={0} aria-label={label}>
                <SparkIcon slug="circle-help" size="small" />
            </span>
        </TooltipTrigger>
    );
}

/**
 * Collapse usecase-attached names into user-facing chips: strip per-subtask
 * ``__t_<6chars>`` clone suffixes, dedupe, and skip anything already shown
 * via a pending local entry (Next → Back keeps uploadName without the clone
 * suffix while usecase.datasets lists the clone).
 */
function visibleUploadedChips(uploadedNames, pendingFiles) {
    const pendingDisplayKeys = new Set(
        (pendingFiles || [])
            .map((entry) => getUserFacingDatasetName(entry.uploadName || '') || entry.uploadName)
            .filter(Boolean)
    );
    const seen = new Set();
    const chips = [];
    for (const name of uploadedNames || []) {
        if (!name) continue;
        const displayName = getUserFacingDatasetName(name) || name;
        if (pendingDisplayKeys.has(displayName) || pendingDisplayKeys.has(name)) {
            continue;
        }
        if (seen.has(displayName)) continue;
        seen.add(displayName);
        chips.push({ displayName, storedName: name });
    }
    return chips;
}

/**
 * In-flight upload card (phase `uploading`) backing a chip, or null.
 * `uploadingByName` is keyed by progress key — the predicted clone name for
 * single-objective files, the raw name for multi-objective uploads — so try
 * the exact names first and fall back to the user-facing (suffix-less) name.
 */
function uploadingInfoFor(uploadingByName, names, displayName) {
    if (!uploadingByName) return null;
    for (const name of names) {
        if (name && uploadingByName[name]) {
            return { progressKey: name, ...uploadingByName[name] };
        }
    }
    if (!displayName) return null;
    const key = Object.keys(uploadingByName).find(
        (progressKey) => (getUserFacingDatasetName(progressKey) || progressKey) === displayName
    );
    return key ? { progressKey: key, ...uploadingByName[key] } : null;
}

const uploadingCopy = (info) => `Uploading… ${Math.round(Number(info?.percent) || 0)}%`;

/**
 * True when a chip's attached name (any of `names`, or its user-facing
 * `displayName`) is being detached from the task right now.
 */
function isRemovingName(removingNames, names, displayName) {
    if (!removingNames?.length) return false;
    return removingNames.some(
        (name) =>
            names.includes(name) ||
            (Boolean(displayName) && (getUserFacingDatasetName(name) || name) === displayName)
    );
}

function chipSizeLabel(entry, labelsLoading) {
    const isLocalPick = Boolean(entry?.file);
    const isLibraryPick = Boolean(entry?.fromLibrary);
    const isHuggingFacePick = Boolean(entry?.fromHuggingFace);
    if (entry?.columnsLoading) return null;
    if (labelsLoading) return null;
    if (isLocalPick) return formatFileSize(entry.file.size);
    if (isLibraryPick) return 'From library';
    if (isHuggingFacePick) return 'From Hugging Face';
    return 'Uploaded';
}

function entryShowsAiCard(entry, usecaseName, { busy } = {}) {
    if (busy) return false;
    return shouldShowAiGeneratedFileCard({
        origin: entry?.origin || entry?.dataCreationType,
        sourceConfig: entry?.sourceConfig,
        datasetName: entry?.uploadName || entry?.datasetName,
        usecaseName,
    });
}

function detailsFromChip(entry) {
    return {
        origin: entry?.origin || entry?.dataCreationType,
        sourceConfig: entry?.sourceConfig,
        createdByUser: entry?.createdByUser,
        createdDatetime: entry?.createdDatetime,
        datasetName: entry?.uploadName || entry?.datasetName,
    };
}

function WizardFileChip({
    displayName,
    sizeContent,
    loading = false,
    aiGenerated = false,
    aiPillLabel,
    fileSize,
    onRemove,
    /** Keeps the trash in place but inert (e.g. while this chip is being removed). */
    removeDisabled = false,
    onInfo,
}) {
    if (aiGenerated && !loading) {
        return (
            <div className="cw-dc-ai-file-wrap">
                <ReadOnlyGuard block>
                    <UploadArea
                        state="success"
                        fileName={displayName}
                        fileSize={fileSize}
                        onRemove={onRemove}
                        onInfo={onInfo}
                        aiGenerated
                        aiPillLabel={aiPillLabel}
                    />
                </ReadOnlyGuard>
            </div>
        );
    }
    return (
        <div className="cw-dc-file-chip">
            <div className="cw-dc-file-chip__info">
                <span className="cw-dc-file-chip__icon">
                    {/* 40px-native glyph — scaling the 24px asset thins the strokes */}
                    <SparkIcon slug="description-large" size="large" />
                </span>
                <div className="cw-dc-file-chip__text">
                    <span className="cw-dc-file-chip__name">{displayName}</span>
                    <span
                        className={`cw-dc-file-chip__size${loading ? ' cw-dc-file-chip__size--loading' : ''}`}
                    >
                        {sizeContent}
                    </span>
                </div>
            </div>
            {onRemove ? (
                <Button
                    size="tiny"
                    variant="secondary"
                    icon={<SparkIcon slug="trash" size="small" />}
                    aria-label={`Remove ${displayName}`}
                    disabled={removeDisabled}
                    disableWhenReadOnly
                    onClick={onRemove}
                />
            ) : null}
        </div>
    );
}

/**
 * Wizard Data Collection upload card (Figma 3001-68298 empty / 3080-5248
 * filled). Empty: dashed drop zone with illustration, tertiary upload button
 * and an optional "Choose From your Uploads" link (`showLibraryLink`, hidden
 * in the create-task wizard). Filled: solid card with dashed file
 * chips (40px file icon, name, size, trash) for every pending pick and every
 * already-uploaded name. AI-generated files use Spark UploadArea (Figma 5132:15780).
 */
const UploadCard = ({
    target,
    slot,
    uploadedNames = [],
    taskType,
    acceptAttribute,
    formatsLabel,
    onPickFiles,
    onRemovePending,
    onRemoveUploaded,
    onOpenLibrary,
    showLibraryLink = true,
    error,
    onDismissError,
    labelsLoading = false,
    usecaseName,
    /** Agentic task: json/jsonl/parquet only (no CSV). */
    isAgentic = false,
    /**
     * `{ [progressKey]: { percent } }` for this card's uploads still in the
     * `uploading` phase (non-blocking Next → Back). Matching chips show
     * "Uploading… NN%" and their trash button cancels via `onCancelUploading`.
     */
    uploadingByName = {},
    /** `(progressKey, entryId | null)` — cancel an in-flight upload chip. */
    onCancelUploading,
    /** Attached names being detached right now; their chips show "Removing…". */
    removingNames = [],
}) => {
    const { readOnly } = useReadOnly();
    const inputRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [formatError, setFormatError] = useState(null);
    const [details, setDetails] = useState(null);
    const copy = CARD_COPY[target];
    const pendingFiles = slot?.files || [];
    const uploadedChips = useMemo(
        () => visibleUploadedChips(uploadedNames, pendingFiles),
        [uploadedNames, pendingFiles]
    );

    const handleFiles = (fileList) => {
        const files = Array.from(fileList || []).filter(Boolean);
        if (!files.length) return;
        const formatOptions = { isAgentic };
        const { supported, unsupported } = partitionFilesBySupport(files, taskType, formatOptions);
        setFormatError(
            unsupported.length ? unsupportedFileTypeCardMessage(taskType, formatOptions) : null
        );
        if (unsupported.length) showUnsupportedFileTypeToast(unsupported, taskType, formatOptions);
        if (supported.length) onPickFiles?.(supported);
    };

    const displayError = formatError || error;
    const canDismissError = Boolean(formatError || onDismissError);

    // Format errors are dismissible (clears the red border/message); the
    // "Please upload a dataset/benchmark" validation error is not — it only
    // clears once data is attached.
    const errorBlock = displayError ? (
        <div className="cw-field-error cw-dc-upload-card__error-row" role="alert">
            <span>{displayError}</span>
            {canDismissError && (
                <button
                    type="button"
                    className="cw-dc-upload-error-dismiss"
                    aria-label="Dismiss error"
                    onClick={() => {
                        if (formatError) setFormatError(null);
                        onDismissError?.();
                    }}
                >
                    <SparkIcon slug="close" size="small" />
                </button>
            )}
        </div>
    ) : null;

    const bindDropHandlers = {
        onDragOver: (e) => {
            e.preventDefault();
            if (!readOnly) setIsDragOver(true);
        },
        onDragLeave: () => setIsDragOver(false),
        onDrop: (e) => {
            e.preventDefault();
            setIsDragOver(false);
            if (!readOnly) handleFiles(e.dataTransfer?.files);
        },
    };

    const fileInput = (
        <input
            ref={inputRef}
            type="file"
            accept={acceptAttribute}
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
                handleFiles(e.target.files);
                e.target.value = '';
            }}
        />
    );

    const hasPending = pendingFiles.length > 0;
    // Keep the filled card when usecase still has attachments even if every
    // name collapsed away as a duplicate of a pending chip.
    const hasUploaded = uploadedChips.length > 0 || uploadedNames.length > 0;
    const help = <UploadCardHelp text={copy.helpText} label={copy.helpLabel} />;
    const extractingCopy =
        taskType === TaskType.NER ? 'Extracting entities…' : 'Extracting labels…';
    if (hasPending || hasUploaded) {
        return (
            <div
                className={`cw-dc-upload-card cw-dc-upload-card--filled${isDragOver ? ' cw-dc-upload-card--drag' : ''}${displayError ? ' cw-dc-upload-card--error' : ''}`}
                {...bindDropHandlers}
            >
                {help}
                <div className="cw-dc-upload-card__filled-title">{copy.filledTitle}</div>
                <div className="cw-dc-file-chip-list">
                    {pendingFiles.map((entry) => {
                        const rawName =
                            entry.uploadName ||
                            (entry.file ? entry.file.name.split('.')[0] : 'File');
                        const displayName = getUserFacingDatasetName(rawName) || rawName;
                        const isLocalPick = Boolean(entry.file);
                        const isLibraryPick = Boolean(entry.fromLibrary);
                        const isHuggingFacePick = Boolean(entry.fromHuggingFace);
                        // Only a submitted entry (File handle dropped) can be in flight.
                        const uploading = isLocalPick
                            ? null
                            : uploadingInfoFor(
                                  uploadingByName,
                                  [entry.datasetName, entry.uploadName],
                                  displayName
                              );
                        const removing =
                            !uploading &&
                            isRemovingName(
                                removingNames,
                                [entry.uploadName, entry.datasetName],
                                displayName
                            );
                        const loading = Boolean(
                            uploading || removing || entry.columnsLoading || labelsLoading
                        );
                        const sizeLabel = chipSizeLabel(entry, labelsLoading);
                        const aiGenerated = entryShowsAiCard(entry, usecaseName, { busy: loading });
                        return (
                            <WizardFileChip
                                key={entry.id || displayName}
                                displayName={displayName}
                                loading={loading}
                                aiGenerated={aiGenerated}
                                aiPillLabel={datasetSourcePillLabel({
                                    origin: entry?.origin || entry?.dataCreationType,
                                    sourceConfig: entry?.sourceConfig,
                                    datasetName: entry?.uploadName || entry?.datasetName,
                                    usecaseName,
                                })}
                                fileSize={sizeLabel || 'Uploaded'}
                                removeDisabled={removing}
                                sizeContent={
                                    uploading ? (
                                        <>
                                            <Spinner size="small" />
                                            {uploadingCopy(uploading)}
                                        </>
                                    ) : removing ? (
                                        <>
                                            <Spinner size="small" />
                                            Removing…
                                        </>
                                    ) : entry.columnsLoading ? (
                                        <>
                                            <Spinner size="small" />
                                            Reading file…
                                        </>
                                    ) : labelsLoading ? (
                                        <>
                                            <Spinner size="small" />
                                            {extractingCopy}
                                        </>
                                    ) : (
                                        sizeLabel
                                    )
                                }
                                onInfo={
                                    aiGenerated ? () => setDetails(detailsFromChip(entry)) : undefined
                                }
                                onRemove={() => {
                                    if (uploading && onCancelUploading) {
                                        // Still uploading: abort + server-side
                                        // cleanup, never the detach-from-task path.
                                        onCancelUploading(uploading.progressKey, entry.id);
                                    } else if (isLocalPick || isLibraryPick || isHuggingFacePick) {
                                        onRemovePending?.(entry.id);
                                    } else if (entry.uploadName) {
                                        onRemoveUploaded?.(entry.uploadName);
                                    } else {
                                        onRemovePending?.(entry.id);
                                    }
                                }}
                            />
                        );
                    })}
                    {uploadedChips.map(({ displayName, storedName }) => {
                        // Optimistically attached name whose upload is still
                        // running (library clone, or a file chip not kept in
                        // the slot) — same in-flight treatment as above.
                        const uploading = uploadingInfoFor(
                            uploadingByName,
                            [storedName],
                            displayName
                        );
                        const removing =
                            !uploading && isRemovingName(removingNames, [storedName], displayName);
                        const aiGenerated =
                            !uploading &&
                            !removing &&
                            shouldShowAiGeneratedFileCard({
                                datasetName: storedName,
                                usecaseName,
                            });
                        const onRemove =
                            uploading && onCancelUploading
                                ? () => onCancelUploading(uploading.progressKey, null)
                                : onRemoveUploaded
                                  ? () => onRemoveUploaded(storedName)
                                  : undefined;
                        return (
                            <WizardFileChip
                                key={storedName}
                                displayName={displayName}
                                loading={Boolean(uploading || removing)}
                                aiGenerated={aiGenerated}
                                aiPillLabel={datasetSourcePillLabel({
                                    datasetName: storedName,
                                    usecaseName,
                                })}
                                fileSize="Uploaded"
                                removeDisabled={removing}
                                sizeContent={
                                    uploading ? (
                                        <>
                                            <Spinner size="small" />
                                            {uploadingCopy(uploading)}
                                        </>
                                    ) : removing ? (
                                        <>
                                            <Spinner size="small" />
                                            Removing…
                                        </>
                                    ) : (
                                        'Uploaded'
                                    )
                                }
                                onInfo={
                                    aiGenerated
                                        ? () =>
                                              setDetails({
                                                  origin: undefined,
                                                  sourceConfig: undefined,
                                              })
                                        : undefined
                                }
                                onRemove={onRemove}
                            />
                        );
                    })}
                </div>
                <Button
                    size="tiny"
                    variant="tertiary"
                    startIcon={<SparkIcon slug="upload" size="small" />}
                    showStartIcon
                    disableWhenReadOnly
                    onClick={() => inputRef.current?.click()}
                >
                    {copy.addMoreLabel}
                </Button>
                {errorBlock}
                {fileInput}
                <DatasetSourceDetailsModal
                    open={Boolean(details)}
                    onClose={() => setDetails(null)}
                    origin={details?.origin}
                    sourceConfig={details?.sourceConfig}
                    createdByUser={details?.createdByUser}
                    createdDatetime={details?.createdDatetime}
                    datasetName={details?.datasetName}
                />
            </div>
        );
    }

    return (
        <div
            className={`cw-dc-upload-card cw-dc-upload-card--empty${isDragOver ? ' cw-dc-upload-card--drag' : ''}${displayError ? ' cw-dc-upload-card--error' : ''}`}
            {...bindDropHandlers}
        >
            {help}
            <img
                className="cw-dc-upload-card__illustration"
                src={copy.illustration}
                alt=""
                aria-hidden="true"
            />
            <div className="cw-dc-upload-card__text">
                <div className="cw-dc-upload-card__title">{copy.emptyTitle}</div>
                <div className="cw-dc-upload-card__subtitle">{formatsLabel}</div>
            </div>
            <div className="cw-dc-upload-card__actions">
                <Button
                    size="medium"
                    variant="tertiary"
                    startIcon={<SparkIcon slug="upload" size="medium" />}
                    showStartIcon
                    disableWhenReadOnly
                    onClick={() => inputRef.current?.click()}
                >
                    {copy.buttonLabel}
                </Button>
                {showLibraryLink && (
                    <ReadOnlyGuard>
                        <button type="button" className="cw-dc-library-link" onClick={onOpenLibrary}>
                            <SparkIcon slug="library" size="small" />
                            <span>Choose From your Uploads</span>
                        </button>
                    </ReadOnlyGuard>
                )}
            </div>
            {errorBlock}
            {fileInput}
        </div>
    );
};

export default UploadCard;
